/**
 * Drop Analyzer — evaluates a proposed (job → crew/day) move BEFORE it lands.
 *
 * Used by the Plan cockpit to render an inline decision hint while a card is
 * being dragged across the capacity grid.
 *
 * Pure, additive: composes existing engines (capacityEngine, dispatchRecommendation,
 * preSpendGate, readiness). Mutates nothing.
 *
 *   verdict   → "valid" | "warning" | "blocked"
 *   reasons   → short human strings ranked by severity
 *   utilDelta → +/- crew-days for the target crew this week (after move)
 *   travel    → "low" | "med" | "high" — proxy via crew home-city match
 *   fitScore  → 0-100 from dispatchRecommendation if crew is a known contractor
 */
import type { Contractor, Job } from "@/data/types";
import type { CapacityGrid } from "./capacityEngine";
import { UNASSIGNED_ROW_KEY } from "./capacityEngine";
import { derivePreSpendGate } from "@/data/preSpendGate";
import { isExternalManagedImport } from "@/data/import/migrationVisibilityGuard";

export type DropVerdict = "valid" | "warning" | "blocked";
export type TravelLevel = "low" | "med" | "high" | "unknown";

export interface DropAnalysis {
  verdict: DropVerdict;
  /** Primary one-line summary — what to render on the cell while hovering. */
  headline: string;
  /** Ranked extra lines — surfaced in tooltip. */
  reasons: string[];
  /** Crew-days delta for this week on the target crew (after the move). */
  utilDelta: number;
  /** New utilization % for target crew. */
  newUtilizationPct: number;
  /** Travel proxy: does the job's city match the crew's most-frequent city? */
  travel: TravelLevel;
  /** Crew fit score 0-100 if available. */
  fitScore: number | null;
  /** Hard blockers — populated only when verdict === "blocked". */
  blockers: string[];
}

interface AnalyzeOpts {
  jobId: string;
  targetCrewName: string;
  targetDateIso: string;
  grid: CapacityGrid;
  jobs: Job[];
  contractors?: Contractor[];
}

export function analyzeDrop(opts: AnalyzeOpts): DropAnalysis {
  const { jobId, targetCrewName, targetDateIso, grid, jobs, contractors = [] } = opts;
  const job = jobs.find(j => j.id === jobId);
  if (!job) {
    return {
      verdict: "blocked",
      headline: "Job not found",
      reasons: [],
      utilDelta: 0,
      newUtilizationPct: 0,
      travel: "unknown",
      fitScore: null,
      blockers: ["Job not found"],
    };
  }

  // Dropping onto the Unassigned row is always valid — no fit/conflict/gate checks.
  if (targetCrewName === UNASSIGNED_ROW_KEY) {
    return {
      verdict: "valid",
      headline: "Remove crew assignment · keep date",
      reasons: [],
      utilDelta: 0,
      newUtilizationPct: 0,
      travel: "unknown",
      fitScore: null,
      blockers: [],
    };
  }

  const targetRow = grid.rows.find(r => r.crewName === targetCrewName);
  if (!targetRow) {
    return {
      verdict: "blocked",
      headline: "Crew not found",
      reasons: [],
      utilDelta: 0,
      newUtilizationPct: 0,
      travel: "unknown",
      fitScore: null,
      blockers: ["Crew not on board"],
    };
  }

  const targetCell = targetRow.cells.find(c => c.dateIso === targetDateIso);
  const blockers: string[] = [];
  const reasons: string[] = [];

  // ── Pre-spend gate ──────────────────────────────────────────────────
  // Hard blocker — same rule as Ready Rail. A job can't be moved onto the
  // grid (or relocated) if its money/legal gates aren't cleared.
  const externallyManagedBypass = isExternalManagedImport(job);
  const gate = derivePreSpendGate(job);
  if (!externallyManagedBypass && !gate.cleared) {
    blockers.push(`Pre-spend gate: ${gate.shortReason ?? "not cleared"}`);
  }

  // ── Cell occupancy (no double-booking) ──────────────────────────────
  const isCurrentSlot =
    job.scheduledInstallDate === targetDateIso &&
    ((job.assignmentType === "Internal Crew" && job.assignedCrew === targetCrewName) ||
     (job.assignmentType === "Subcontractor" && job.assignedSubcontractor === targetCrewName));

  if (targetCell && targetCell.state !== "open" && !isCurrentSlot) {
    blockers.push("Slot already booked");
  }

  // ── Fit score (from existing capacity engine) ───────────────────────
  // Look up the recommendation surface stored on the engine pass.
  const fitScore = findFitScore(grid, jobId, targetCrewName);

  // ── Utilization delta ───────────────────────────────────────────────
  // If moving FROM another cell on the same crew (cell-to-cell intra-crew),
  // delta = 0. Otherwise +1 booked day.
  const wasOnTargetCrewThisWeek = targetRow.cells.some(
    c => c.job?.id === jobId && c.dateIso !== targetDateIso,
  );
  const utilDelta = wasOnTargetCrewThisWeek ? 0 : 1;
  const newBooked = targetRow.bookedDays + utilDelta - (isCurrentSlot ? 1 : 0);
  const newUtilizationPct = Math.min(
    150,
    Math.round((newBooked / Math.max(1, targetRow.capacityDays)) * 100),
  );

  if (newUtilizationPct > 100) {
    reasons.push(`Crew overloaded (${newBooked}/${targetRow.capacityDays} days)`);
  }

  // ── Travel proxy ────────────────────────────────────────────────────
  // Crew's most-frequent city this week. If job's city matches → low.
  const inDeclaredCoverage = isInDeclaredContractorCoverage(targetCrewName, job, contractors);
  const travel = inDeclaredCoverage ? "low" : computeTravelLevel(targetRow, job);
  if (travel === "high") {
    // Out-of-area is a high-impact decision: surface it as the headline reason
    // so it always wins the warning slot, ahead of softer overload notes.
    reasons.unshift(`Out-of-area assignment · ${job.address.city} is far from this crew`);
  } else if (travel === "med") {
    reasons.push(`Cross-metro: ${job.address.city}`);
  }

  // ── Verdict ─────────────────────────────────────────────────────────
  let verdict: DropVerdict;
  let headline: string;
  if (blockers.length > 0) {
    verdict = "blocked";
    headline = blockers[0];
  } else if (reasons.length > 0) {
    verdict = "warning";
    headline = reasons[0];
  } else {
    verdict = "valid";
    const fitText = fitScore !== null ? ` · fit ${fitScore}` : "";
    headline = utilDelta === 0
      ? `Move within ${targetCrewName}${fitText}`
      : `Good fit · +${utilDelta} day${fitText}`;
  }

  return {
    verdict, headline, reasons,
    utilDelta, newUtilizationPct,
    travel, fitScore, blockers,
  };
}

// ── helpers ──────────────────────────────────────────────────────────────

function findFitScore(grid: CapacityGrid, jobId: string, crewName: string): number | null {
  // Engine doesn't expose the per-(job,crew) map publicly; scan rows for any
  // existing cell that already holds this job on this crew → carry its fit.
  // For a brand-new placement we fall back to "unknown" (null).
  const row = grid.rows.find(r => r.crewName === crewName);
  if (!row) return null;
  const knownCell = row.cells.find(c => c.job?.id === jobId);
  return knownCell?.fitScore ?? null;
}

function computeTravelLevel(
  row: CapacityGrid["rows"][number],
  job: Job,
): TravelLevel {
  const cities = new Map<string, number>();
  for (const cell of row.cells) {
    const c = cell.job?.address?.city;
    if (!c) continue;
    cities.set(c, (cities.get(c) || 0) + 1);
  }
  if (cities.size === 0) return "unknown";
  let home = "", max = 0;
  for (const [c, n] of cities) {
    if (n > max) { max = n; home = c; }
  }
  if (job.address.city === home) return "low";

  // Distance-based check (miles) — supersedes the metro-cluster proxy when
  // we have coordinates for both cities. Thresholds tuned for FL service area:
  //   ≤ 25 mi → low (same metro)
  //   ≤ 60 mi → med (acceptable but flag)
  //   >  60 mi → high (clear out-of-area warning)
  const homeCoords = CITY_COORDS[home];
  const jobCoords  = CITY_COORDS[job.address.city];
  if (homeCoords && jobCoords) {
    const miles = haversineMiles(homeCoords, jobCoords);
    if (miles <= 25) return "low";
    if (miles <= 60) return "med";
    return "high";
  }

  // Fallback to metro proxy when coords are missing.
  const TAMPA_METRO = new Set(["Tampa", "St. Petersburg", "Clearwater", "Brandon", "Riverview", "Largo"]);
  const SWFL_METRO = new Set(["Fort Myers", "Cape Coral", "Naples", "Bradenton", "Sarasota"]);
  const sameMetro = (
    (TAMPA_METRO.has(home) && TAMPA_METRO.has(job.address.city)) ||
    (SWFL_METRO.has(home) && SWFL_METRO.has(job.address.city))
  );
  return sameMetro ? "med" : "high";
}

function normalizeCoverageText(value: unknown): string {
  return String(value ?? "").trim().toLowerCase();
}

function contractorMatchesCrew(contractor: Contractor, crewName: string): boolean {
  const crew = normalizeCoverageText(crewName);
  return Boolean(
    crew &&
    (
      normalizeCoverageText(contractor.companyName) === crew ||
      normalizeCoverageText(contractor.contactName) === crew
    )
  );
}

function isInDeclaredContractorCoverage(
  crewName: string,
  job: Job,
  contractors: Contractor[],
): boolean {
  const contractor = contractors.find((c) => contractorMatchesCrew(c, crewName));
  if (!contractor) return false;

  const city = normalizeCoverageText(job.address?.city);
  const state = normalizeCoverageText(job.address?.state);
  const zip = normalizeCoverageText(job.address?.zip);
  const cityState = [city, state].filter(Boolean).join(", ");
  const coverageTexts = [
    contractor.serviceArea,
    ...(contractor.serviceRegions ?? []),
  ].map(normalizeCoverageText).filter(Boolean);

  const regionMatch = Boolean(city) && coverageTexts.some((text) =>
    text === city ||
    text.includes(city) ||
    (cityState && text.includes(cityState)),
  );
  const zipMatch = Boolean(zip) && (contractor.serviceZipCodes ?? [])
    .map(normalizeCoverageText)
    .some((coverageZip) => coverageZip === zip || zip.startsWith(coverageZip));

  return regionMatch || zipMatch;
}

// ── Florida service-area coordinates (mirror of PlanCockpit's CITY_COORDS).
//    Kept colocated so the analyzer stays self-contained.
const CITY_COORDS: Record<string, [number, number]> = {
  "Tampa": [27.9506, -82.4572],
  "St. Petersburg": [27.7676, -82.6403],
  "Clearwater": [27.9659, -82.8001],
  "Sarasota": [27.3364, -82.5307],
  "Bradenton": [27.4989, -82.5748],
  "Fort Myers": [26.6406, -81.8723],
  "Cape Coral": [26.5629, -81.9495],
  "Naples": [26.1420, -81.7948],
  "Lakeland": [28.0395, -81.9498],
  "Brandon": [27.9378, -82.2860],
  "Riverview": [27.8661, -82.3265],
  "Largo": [27.9095, -82.7873],
};

function haversineMiles(a: [number, number], b: [number, number]): number {
  const R = 3958.8;
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]);
  const dLon = toRad(b[1] - a[1]);
  const lat1 = toRad(a[0]);
  const lat2 = toRad(b[0]);
  const h = Math.sin(dLat / 2) ** 2 + Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.sqrt(h));
}
